<template>
  <header
    class="flex h-14 items-center gap-4 border-b bg-muted/40 px-6 dark:bg-background"
  >
    <h1 v-if="showLogo">
      <img
        v-if="isVnimpexOrganization"
        src="/img/vnimpex-logo.png"
        alt="VN-Impex Logo"
        width="40"
        height="40"
      />
      <template v-else>
        <svg
          width="154"
          height="40"
          viewBox="0 0 1000 260"
          xmlns="http://www.w3.org/2000/svg"
          class="hidden min-[600px]:block"
        >
          <use xlink:href="/img/digitax-logo.svg#logo" />
        </svg>
        <img
          src="/img/digitax-logo-notext.svg"
          alt="Digitax Logo"
          width="40"
          height="40"
          class="block min-[600px]:hidden"
        />
      </template>
    </h1>
    <div class="flex-1 items-center gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger as-child>
          <Button variant="ghost" class="-ml-2 h-auto p-1 px-2">
            <div class="flex items-center gap-2">
              <span
                class="hidden text-xs font-medium uppercase tracking-wider text-muted-foreground sm:flex"
              >
                {{ t("common.organization") }}:
              </span>
              <h2 class="truncate text-lg font-semibold">
                {{ currentOrganization?.name }}
              </h2>
              <ChevronDown class="h-4 w-4 text-muted-foreground" />
            </div>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          class="w-64 max-w-[calc(100vw-2rem)]"
        >
          <DropdownMenuLabel
            class="px-2 py-1 text-xs font-normal text-muted-foreground"
          >
            {{ t("organizationDropdown.selectOrganization") }}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            <DropdownMenuItem
              v-for="org in organizations"
              :key="org.id"
              class="cursor-pointer"
              @click="selectOrganization(org.id)"
            >
              <div class="flex w-full items-center justify-between">
                <span class="truncate">{{ org.name }}</span>
                <Check
                  v-if="org.id === organizationId"
                  class="ml-2 h-4 w-4 text-primary"
                />
              </div>
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>

    <div class="ml-auto flex items-center gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger as-child>
          <Button variant="ghost" size="icon" class="rounded-full">
            <Settings class="h-5 w-5" />
            <span class="sr-only">{{ t("sidebar.settings") }}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" class="w-64 max-w-[calc(100vw-2rem)]">
          <DropdownMenuLabel>{{ t("sidebar.settings") }}</DropdownMenuLabel>
          <DropdownMenuSeparator />

          <DropdownMenuGroup>
            <DropdownMenuItem
              v-for="page in settingsPages"
              :key="page.key"
              class="cursor-pointer"
              @click="openSettings(page.key)"
            >
              <component :is="page.icon" class="mr-2 h-4 w-4" />
              <span>{{ t(page.label) }}</span>
            </DropdownMenuItem>
          </DropdownMenuGroup>

          <DropdownMenuSeparator />

          <DropdownMenuItem class="cursor-pointer" @click="signOut">
            <LogOutIcon class="mr-2 h-4 w-4" />
            <span>{{ t("appSettingsDropdown.logout") }}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>

    <SettingsDialog
      v-model:open="settingsOpen"
      v-model:current-page="currentPage"
    />
  </header>
</template>

<script setup lang="ts">
import { Settings, Check, LogOutIcon, ChevronDown } from "lucide-vue-next";
import { SETTINGS_PAGES } from "~/utils/settings";
import { useApiOrganizations } from "~/composables/api";
import SettingsDialog from "~/components/settings/SettingsDialog.vue";

defineProps<{
  showLogo?: boolean;
}>();

const { organizationId, setOrganizationId } = useOrganizationId();
const { data: organizations } = await useApiOrganizations();
const { t } = useI18n();

const settingsOpen = ref(false);
const currentPage = ref("general");

const currentOrganization = computed(() => {
  return organizations.value?.find((org) => org.id === organizationId.value);
});

// Org 1 is the real VN-IMPEX pilot; every other org (Unternehmen X demo
// included) shows the generic Digitax logo.
const isVnimpexOrganization = computed(() => organizationId.value === 1);

const settingsPages = SETTINGS_PAGES;

const selectOrganization = (orgId: number) => {
  if (orgId !== organizationId.value) {
    setOrganizationId(orgId);
  }
};

const openSettings = (pageKey: string = "general") => {
  currentPage.value = pageKey;
  settingsOpen.value = true;
};

async function signOut() {
  window.location.href = "/auth/logout";
}
</script>
